'use client'

import { useRef, useState } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Mousewheel, Navigation, A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'

import IconButton from '/react4xp/components/prototype/IconButton'
import ArrowRightSolid from '/react4xp/components/prototype/icons/ArrowRightSolid'

import type { ListPeopleProps, PersonCard } from './types'

function PersonCardItem({ photo, name, jobTitle, location, email, phone }: PersonCard) {
	return (
		<div className="flex flex-col gap-2 pb-6">
			<figure className="w-full max-w-full aspect-square overflow-hidden rounded-sm">
				{photo && <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" />}
			</figure>

			<h3 className="h4 text-pure-white line-clamp-2">{name}</h3>

			{(jobTitle || location) && (
				<div className="flex flex-col">
					{jobTitle && <p className="body text-pure-white truncate">{jobTitle}</p>}
					{location && <p className="body text-pure-white truncate">{location}</p>}
				</div>
			)}

			{(email || phone) && (
				<div className="flex flex-col items-start">
					{email && (
						<a
							href={`mailto:${email}`}
							className="button-text text-pure-white flex h-11 items-center max-w-full min-w-0 underline decoration-brand-500 underline-offset-3 decoration-[length:7%] [text-decoration-skip-ink:none] transition-colors hover:decoration-current"
						>
							<span className="truncate pt-1 pb-1">{email}</span>
						</a>
					)}
					{phone && (
						<a
							href={`tel:${phone.replace(/\s+/g, '')}`}
							className="button-text text-pure-white flex h-11 items-center max-w-full min-w-0 underline decoration-brand-500 underline-offset-3 decoration-[length:7%] [text-decoration-skip-ink:none] transition-colors hover:decoration-current"
						>
							<span className="truncate pt-1 pb-1">{phone}</span>
						</a>
					)}
				</div>
			)}
		</div>
	)
}

export default function ListPeople({
	overline,
	title,
	people = [],
	prevLabel = 'Previous slide',
	nextLabel = 'Next slide',
}: ListPeopleProps) {
	const swiperRef = useRef<SwiperType | null>(null)
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)

	const isSingle = people.length === 1
	const isDesktopCarousel = people.length > 3

	function handleSwiperUpdate(swiper: SwiperType) {
		setIsBeginning(swiper.isBeginning)
		setIsEnd(swiper.isEnd)
	}

	return (
		<section className="bg-black-950 py-12 overflow-hidden">
			<div className="container">
				<div className="flex flex-col gap-0">
					{overline && <p className="body text-pure-white">{overline}</p>}
					{title && <h2 className="h2 text-pure-white">{title}</h2>}
				</div>

				<div className="flex flex-col gap-8 mt-6 md:mt-8">
					{/* Static row — always grid-cols-3, even with 1-2 people; empty tracks render nothing. Desktop/tablet only, and only when the carousel isn't triggered. */}
					{!isDesktopCarousel && (
						<div className="hidden md:grid md:grid-cols-3 md:gap-8">
							{people.map((person, i) => (
								<PersonCardItem key={i} {...person} />
							))}
						</div>
					)}

					{/* Single card — no carousel needed, renders full-width within .container on mobile */}
					{isSingle && (
						<div className="md:hidden">
							<PersonCardItem {...people[0]} />
						</div>
					)}

					{/* Carousel — on mobile once people.length > 1, and on desktop/tablet once people.length > 3 */}
					{!isSingle && (
						<div className={isDesktopCarousel ? 'flex flex-col gap-8' : 'flex flex-col gap-8 md:hidden'}>
							<div className="flex justify-start md:justify-end">
								<span className="flex items-center gap-2">
									<IconButton
										icon={ArrowRightSolid}
										label={prevLabel}
										variant="round"
										disabled={isBeginning}
										onClick={() => swiperRef.current?.slidePrev()}
										className="rotate-180"
									/>
									<IconButton
										icon={ArrowRightSolid}
										label={nextLabel}
										variant="round"
										disabled={isEnd}
										onClick={() => swiperRef.current?.slideNext()}
									/>
								</span>
							</div>

							{/* Bleeds to the viewport edge below 2xl (1536px); at 2xl+ it sits flush with
							    .container again, same as the rest of the section. slidesOffsetBefore/After
							    (matching .container's px-3/md:px-6/lg:px-16 scale, cleared at 2xl) keep the
							    first/last card aligned with the heading above — using Swiper's own offset
							    params rather than CSS padding on .swiper-wrapper so isBeginning/isEnd and
							    the max scroll distance stay correct. */}
							<div className="carousel-track full-bleed min-w-0 2xl:w-full 2xl:mx-0">
								<Swiper
									modules={[Mousewheel, Navigation, A11y]}
									mousewheel={{ forceToAxis: true }}
									slidesPerView={1.14}
									spaceBetween={32}
									slidesOffsetBefore={12}
									slidesOffsetAfter={12}
									breakpoints={{
										768: { slidesPerView: 2.14, slidesOffsetBefore: 24, slidesOffsetAfter: 24 },
										1024: { slidesPerView: 3.2, slidesOffsetBefore: 64, slidesOffsetAfter: 64 },
										1536: { slidesPerView: 3.2, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
									}}
									onSwiper={(swiper) => {
										swiperRef.current = swiper
										handleSwiperUpdate(swiper)
									}}
									onSlideChange={(swiper) => handleSwiperUpdate(swiper)}
									onReachBeginning={(swiper) => handleSwiperUpdate(swiper)}
									onReachEnd={(swiper) => handleSwiperUpdate(swiper)}
									onFromEdge={(swiper) => handleSwiperUpdate(swiper)}
								>
									{people.map((person, i) => (
										<SwiperSlide key={i}>
											<PersonCardItem {...person} />
										</SwiperSlide>
									))}
								</Swiper>
							</div>
						</div>
					)}
				</div>
			</div>
		</section>
	)
}
